<script setup lang="ts">
import type { CompletionConsideration } from '@year13/kui/composables/useCompletionSteps';

import { Heading } from '~ds/components/ui/typography';
import { useI18n } from 'vue-i18n';

import KUIUniCompletionConsiderationCard from './KUIUniCompletionConsiderationCard.vue';

interface Props {
  considerations: CompletionConsideration[];
  animationsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  animationsEnabled: true,
});

const emit = defineEmits<{
  /** Fired when the screen mounts — i.e. the consideration step is actually viewed. */
  viewed: [];
}>();

const { t, locale } = useI18n();

// A single consolidated heading listing every affected area, e.g.
// "You have increased your knowledge around Engineering and Creative Arts!".
// Intl.ListFormat handles the locale-correct conjunction grammar for 1, 2, or N areas.
const areaLabels = computed(() =>
  new Intl.ListFormat(locale.value, { type: 'conjunction' }).format(
    props.considerations.map(consideration => consideration.areaLabel),
  ),
);

onMounted(() => emit('viewed'));
</script>

<template>
  <div class="flex w-full max-w-sm flex-col items-center gap-10">
    <Heading
      size="xl"
      as="h2"
      class="text-center leading-tight"
    >
      {{ t('unified.activity.completion.consideration.heading', { area: areaLabels }) }}
    </Heading>
    <KUICoreListRenderer class="flex w-full flex-col gap-10">
      <KUIUniCompletionConsiderationCard
        v-for="consideration in considerations"
        :key="consideration.areaId"
        :area-label="consideration.areaLabel"
        :color="consideration.color"
        :before-level="consideration.beforeLevel"
        :before-progress="consideration.beforeProgress"
        :after-level="consideration.afterLevel"
        :after-progress="consideration.afterProgress"
        :illustration="consideration.illustration"
        :animations-enabled="animationsEnabled"
      />
    </KUICoreListRenderer>
  </div>
</template>
